import { DatePipe } from '@angular/common';
import { Injectable, Pipe, PipeTransform } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Student } from 'src/app/modules/lms/learningpaths/models/interfaces';
import { EmployeeService } from 'src/app/shared/personnel-files/services/employee.service';
import { SysLanguage } from '../../general-enums/SysLanguage';

@Pipe({
  name: 'employeeDate',
})
@Injectable({
  providedIn: 'root',
})
export class EmployeeDatePipe implements PipeTransform {
  constructor(
    private employeeService: EmployeeService,
    private datePipe: DatePipe
  ) { }

  /**
   * Transforms the date into either: 
   * - EmployeeDate taken from the personel files
   * - A custom format
   * 
   * The date follows the `locale` of the signed in user.  
   * `languageId = 1` becomes locale: `nl-BE`
   * 
   * @param value The date to format
   * @param format Custom format.
   * 
   * @example
   * ```typescript
   * transform(new Date(), '[SlashDate] HH:mm'); //-> dd/MM/yyyy HH:mm -> 15/01/2026 16:23
   * ```
   * ```typescript
   * transform(new Date(), '[DashDate] HH:mm'); //-> dd-MM-yyyy HH:mm -> 15-01-2026 16:23
   * ```
   * ```typescript
   * transform(new Date()); //-> dd/MM/yyyy -> 15/01/2026
   * ```
   * 
   * @returns Formatted date.
   */
  public transform(value: Date | string, format?: string): Observable<any> {
    return this.employeeService.getCurrentEmployee().pipe(
      map((employee: Student) => {
        try {

          if (value && (<any>value).length) {
            value = new Date(value);
            if (value.getFullYear() == 1900 && value.getMonth() == 0 && value.getDate() == 1) {
              return '';
            }
          }

          const dateFormat = this.replaceTemplates(format, employee.dateFormat.includes('dd/MM/yyyy')) ?? employee.dateFormat;

          return this.datePipe.transform(value, dateFormat, '', this.LanguageIdToLocale(employee.languageId));
        } catch (e) {
          return 'INVALID DATE ' + value;
        }
      })
    );
  }

  private replaceTemplates(format?: string, isDMY?: boolean) {
    return format
      ?.replace("[SlashDate]", isDMY ? 'dd/MM/yyyy' : 'MM/dd/yyyy')
      ?.replace("[DashDate]", isDMY ? 'dd-MM-yyyy' : 'MM-dd-yyyy')
  }

  private LanguageIdToLocale(languageId: number) {
    switch (languageId) {
      case SysLanguage.Dutch:
        return "nl-BE";
      case SysLanguage.French:
        return "fr-FR";
      case SysLanguage.English:
        return "en-US";
      case SysLanguage.German:
        return "de-DE";
      case SysLanguage.Spanish:
        return "es-ES";
      case SysLanguage.Italian:
        return "it-IT";
      case SysLanguage.Arab:
        return "ar-AE";
      case SysLanguage.Bulgarian:
        return "bg-BG";
      case SysLanguage.Catalan:
        return "ca-AD";
      case SysLanguage.Chinese:
        return "zh-CN";
      case SysLanguage.Danish:
        return "da-DK";
      case SysLanguage.Estonian:
        return "et-EE";
      case SysLanguage.Finnish:
        return "fi-FI";
      case SysLanguage.Greek:
        return "el-GR";
      case SysLanguage.Hungarian:
        return "hu-HU";
      case SysLanguage.Japanese:
        return "ja-JP";
      case SysLanguage.Croatian:
        return "hr-HR";
      case SysLanguage.Latvian:
        return "lv-LV";
      case SysLanguage.Lithuanian:
        return "lt-LT";
      case SysLanguage.Maltese:
        return "mt-MT";
      case SysLanguage.Mandarin:
        return "zh-CN";
      case SysLanguage.Norwegian:
        return "no-NO";
      case SysLanguage.Polish:
        return "pl-PL";
      case SysLanguage.Portuguese:
        return "pt-PT";
      case SysLanguage.Romanian:
        return "ro-RO";
      case SysLanguage.Russian:
        return "ru-RU";
      case SysLanguage.Slovenian:
        return "sl-SI";
      case SysLanguage.Slovak:
        return "sk-SK";
      case SysLanguage.Czech:
        return "cs-CZ";
      case SysLanguage.Turkish:
        return "tr-TR";
      case SysLanguage.South_American_Spanish:
        return "es-AR";
      case SysLanguage.Swedish:
        return "sv-AX";
      default:
        return "en-US";
    }
  }
}
